/**
 * Iter 67bh — Per-car date-range rate rules editor.
 *
 * Lives inside the car edit screen (CarForm), shown only when editing an
 * existing car since a new car has no id yet. Each rule sets a flat €/day
 * for its [start, end] window; shortest matching range wins at booking time.
 *
 * The editor is intentionally compact: a table with inline-edit cells +
 * one "Add rule" button. No modal, no extra round-trip — vendors managing
 * dozens of rules want speed, not ceremony.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, CalendarRange, Loader2, Info } from "lucide-react";
import AttributionChips from "@/components/AttributionChips";

// 8-step deterministic palette so the first 8 rules each get a distinct hue.
// Beyond 8, the cycle repeats — fine for a preview strip; the tooltip always
// shows the rule name + price so adjacent same-coloured cells are still
// distinguishable.
const RULE_PALETTE = [
  { bg: "bg-violet-400",  ring: "ring-violet-500"  },
  { bg: "bg-rose-400",    ring: "ring-rose-500"    },
  { bg: "bg-emerald-400", ring: "ring-emerald-500" },
  { bg: "bg-amber-400",   ring: "ring-amber-500"   },
  { bg: "bg-sky-400",     ring: "ring-sky-500"     },
  { bg: "bg-fuchsia-400", ring: "ring-fuchsia-500" },
  { bg: "bg-teal-400",    ring: "ring-teal-500"    },
  { bg: "bg-orange-400",  ring: "ring-orange-500"  },
];

const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function _pickShortestRule(rules, dayISO) {
  // Mirrors backend `_pick_rate_rule_for_date`: shortest span wins, ties by
  // newest `created_at`. Keeps the preview honest with what the booker will
  // actually see at quote time.
  let best = null;
  let bestSpan = Infinity;
  let bestCreated = "";
  for (const r of rules) {
    if (!r.start_date || !r.end_date) continue;
    if (dayISO < r.start_date || dayISO > r.end_date) continue;
    const span = (new Date(r.end_date) - new Date(r.start_date)) / 86400000;
    const created = r.created_at || "";
    if (span < bestSpan || (span === bestSpan && created > bestCreated)) {
      best = r;
      bestSpan = span;
      bestCreated = created;
    }
  }
  return best;
}

function _ymd(d) {
  return d.toISOString().slice(0, 10);
}

function RateCalendarPreview({ rules, basePricePerDay }) {
  // 60 calendar days starting from today. Computed once per `rules` change
  // since `Date.now()` doesn't move during a single editor session.
  const cells = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const out = [];
    // Map rule.id -> palette slot (stable across renders).
    const paletteByRule = {};
    let idx = 0;
    for (const r of rules) {
      if (!(r.id in paletteByRule)) {
        paletteByRule[r.id] = RULE_PALETTE[idx % RULE_PALETTE.length];
        idx += 1;
      }
    }
    for (let i = 0; i < 60; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const iso = _ymd(d);
      const rule = _pickShortestRule(rules, iso);
      out.push({
        date: d,
        iso,
        rule,
        tone: rule ? paletteByRule[rule.id] : null,
        price: rule ? Number(rule.daily_price_eur) : Number(basePricePerDay || 0),
        isToday: i === 0,
        // First day of a month → render the month label above the cell.
        monthLabel: d.getDate() === 1 || i === 0 ? `${MONTH_LABELS[d.getMonth()]}` : null,
      });
    }
    return out;
  }, [rules, basePricePerDay]);

  // Build month-spanning headers so the user reads "Aug · Sep · Oct" above the strip.
  const monthGroups = useMemo(() => {
    const groups = [];
    let current = null;
    for (const c of cells) {
      const key = `${c.date.getFullYear()}-${c.date.getMonth()}`;
      if (!current || current.key !== key) {
        current = { key, label: MONTH_LABELS[c.date.getMonth()], span: 1 };
        groups.push(current);
      } else {
        current.span += 1;
      }
    }
    return groups;
  }, [cells]);

  const coveredDays = cells.filter((c) => c.rule).length;
  const totalDays = cells.length;

  return (
    <div className="rounded-xl border border-violet-100 bg-gradient-to-br from-violet-50/40 to-white p-3.5" data-testid="rate-calendar-preview">
      <div className="flex items-center justify-between mb-2.5">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700">Next 60 days · preview</div>
          <div className="text-xs text-neutral-600 mt-0.5">{coveredDays}/{totalDays} days covered by a custom rule · remaining days use base €{Number(basePricePerDay || 0).toFixed(2)}/day + seasonal multipliers</div>
        </div>
      </div>

      {/* Month-spanning header row */}
      <div className="flex gap-px text-[9px] uppercase tracking-wider font-bold text-neutral-500 mb-1">
        {monthGroups.map((g) => (
          <div key={g.key} className="text-left" style={{ flexBasis: `${(g.span / totalDays) * 100}%`, minWidth: 0 }}>
            <span className="truncate">{g.label}</span>
          </div>
        ))}
      </div>

      {/* The strip itself */}
      <div className="flex gap-px" data-testid="rate-calendar-strip">
        {cells.map((c) => {
          const isWeekend = c.date.getDay() === 0 || c.date.getDay() === 6;
          let cellClass;
          if (c.rule) {
            cellClass = `${c.tone.bg} hover:opacity-80`;
          } else if (isWeekend) {
            // Weekends without a rule are slightly darker so vendors can read the cadence.
            cellClass = "bg-neutral-200 hover:bg-neutral-300";
          } else {
            cellClass = "bg-neutral-100 hover:bg-neutral-200";
          }
          const todayRing = c.isToday ? "ring-2 ring-offset-1 ring-violet-900" : "";
          const title = `${c.iso}${c.rule ? ` · ${c.rule.name || "unnamed"} · €${c.price.toFixed(2)}/day` : ` · base €${c.price.toFixed(2)}/day`}`;
          return (
            <div
              key={c.iso}
              className={`h-7 flex-1 rounded-sm transition-opacity ${cellClass} ${todayRing} cursor-help`}
              title={title}
              data-testid={`rate-calendar-cell-${c.iso}`}
            />
          );
        })}
      </div>

      {/* Legend — one chip per rule actually visible in the 60-day window. */}
      {(() => {
        const seen = new Set();
        const chips = [];
        for (const c of cells) {
          if (c.rule && !seen.has(c.rule.id)) {
            seen.add(c.rule.id);
            chips.push(c);
          }
        }
        if (chips.length === 0) return null;
        return (
          <div className="flex flex-wrap gap-1.5 mt-3" data-testid="rate-calendar-legend">
            {chips.map((c) => (
              <span key={c.rule.id} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white ring-1 ring-neutral-200 text-[11px]">
                <span className={`h-2 w-2 rounded-sm ${c.tone.bg}`} />
                <span className="font-semibold text-neutral-900 truncate max-w-[140px]">{c.rule.name || "unnamed"}</span>
                <span className="text-neutral-500 tabular-nums">€{Number(c.rule.daily_price_eur).toFixed(2)}/day</span>
              </span>
            ))}
          </div>
        );
      })()}
    </div>
  );
}

function _newRule() {
  const today = new Date();
  const nextMonth = new Date();
  nextMonth.setDate(today.getDate() + 30);
  return {
    name: "",
    start_date: today.toISOString().slice(0, 10),
    end_date: nextMonth.toISOString().slice(0, 10),
    daily_price_eur: "",
  };
}

export default function CarRateRulesEditor({ carId, basePricePerDay, carImage, carName }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(_newRule());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/vendor/cars/${carId}/rate-rules`);
      setRules(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not load rate rules.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!carId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carId]);

  const onAdd = async () => {
    if (!draft.start_date || !draft.end_date) { toast.error("Pick a date range."); return; }
    if (!draft.daily_price_eur || Number(draft.daily_price_eur) <= 0) { toast.error("Daily price must be > 0."); return; }
    if (draft.end_date < draft.start_date) { toast.error("End date must be on or after start."); return; }
    setSaving(true);
    try {
      await api.post(`/vendor/cars/${carId}/rate-rules`, {
        name: draft.name.trim() || null,
        start_date: draft.start_date,
        end_date: draft.end_date,
        daily_price_eur: Number(draft.daily_price_eur),
      });
      setDraft(_newRule());
      setAdding(false);
      toast.success("Rate rule added.");
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Failed to add rule.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (rule) => {
    const label = rule.name || `${rule.start_date} → ${rule.end_date}`;
    if (!window.confirm(`Delete rate rule "${label}"?`)) return;
    setDeletingId(rule.id);
    try {
      await api.delete(`/vendor/cars/${carId}/rate-rules/${rule.id}`);
      toast.success("Rate rule deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Delete failed.");
    } finally {
      setDeletingId(null);
    }
  };

  const showCarContext = Boolean(carImage || carName);

  return (
    <div className="space-y-3" data-testid="car-rate-rules-editor">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {/* Iter 67bl — Car thumbnail + name so the vendor never loses track of
              which vehicle these rules apply to. Hidden when the parent already
              renders its own context strip (e.g. VendorRatesTab). */}
          {showCarContext && (
            carImage ? (
              <img
                src={carImage}
                alt={carName || "Vehicle"}
                className="h-14 w-20 rounded-lg object-cover ring-1 ring-violet-200 shrink-0"
                data-testid="car-rate-rules-photo"
              />
            ) : (
              <div className="h-14 w-20 rounded-lg bg-violet-50 ring-1 ring-violet-200 flex items-center justify-center shrink-0">
                <CalendarRange className="h-5 w-5 text-violet-400" />
              </div>
            )
          )}
          <div className="min-w-0">
            <Label className="text-base font-semibold inline-flex items-center gap-2"><CalendarRange className="h-4 w-4 text-violet-600" /> Date-range rates</Label>
            {showCarContext && carName && (
              <div className="text-[11px] uppercase tracking-wider font-bold text-violet-700 mt-0.5 truncate" data-testid="car-rate-rules-car-name">{carName}</div>
            )}
            <div className="text-xs text-neutral-500 mt-0.5">Override the base price (<strong>€{Number(basePricePerDay || 0).toFixed(2)}/day</strong>) for specific date windows. Shortest matching range wins.</div>
          </div>
        </div>
        {!adding && (
          <Button type="button" variant="outline" size="sm" className="rounded-full shrink-0" onClick={() => setAdding(true)} data-testid="car-rate-rule-add">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add rule
          </Button>
        )}
      </div>

      {!loading && rules.length === 0 && !adding && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-violet-50/50 border border-violet-100 text-xs text-violet-900">
          <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          <span>No custom rates yet. Bookings use the base daily price plus your vendor-wide seasonal multipliers.</span>
        </div>
      )}

      {!loading && rules.length > 0 && (
        <RateCalendarPreview rules={rules} basePricePerDay={basePricePerDay} />
      )}

      {loading && (
        <div className="text-center py-4 text-neutral-500 text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…</div>
      )}

      {/* Iter 69 — refactored from <table> to a card list. The table layout
          collapsed inside the narrow Edit Car dialog (max-w-2xl ≈ 624px of
          content) because Chrome's native <input type="date"> needs ~140px
          and we had it crammed into a w-32 cell with px-3 padding. Cards
          let each field breathe and reflow on narrow widths. */}
      {(rules.length > 0 || adding) && (
        <div className="space-y-2" data-testid="car-rate-rules-list">
          {rules.map((r) => (
            <div
              key={r.id}
              className="rounded-xl border border-border bg-white p-3 flex flex-wrap items-center gap-3"
              data-testid={`car-rate-rule-${r.id}`}
            >
              <div className="flex-1 min-w-[160px]">
                <div className="text-sm font-semibold text-neutral-900 truncate">
                  {r.name || <span className="text-neutral-400 italic font-normal">unnamed</span>}
                </div>
                <div className="text-[11px] text-neutral-500 tabular-nums mt-0.5">
                  {r.start_date} <span className="text-neutral-400">→</span> {r.end_date}
                </div>
                <AttributionChips entity={r} actions={["create", "update"]} compact className="mt-1" />
              </div>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-violet-100 ring-1 ring-violet-200 text-violet-900 font-bold tabular-nums text-sm shrink-0">
                €{Number(r.daily_price_eur).toFixed(2)}
                <span className="text-[9px] font-semibold opacity-70">/day</span>
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-rose-600 shrink-0"
                onClick={() => onDelete(r)}
                disabled={deletingId === r.id}
                data-testid={`car-rate-rule-delete-${r.id}`}
                aria-label="Delete rate rule"
              >
                {deletingId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              </Button>
            </div>
          ))}

          {adding && (
            <div
              className="rounded-xl border border-violet-200 bg-violet-50/40 p-3 sm:p-4 space-y-3"
              data-testid="car-rate-rule-new-row"
            >
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-bold text-violet-700 mb-1 block">Rule name <span className="text-neutral-400 font-normal normal-case tracking-normal">(optional)</span></Label>
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="e.g. Christmas peak"
                  className="h-9 text-sm bg-white"
                  data-testid="car-rate-rule-name"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-bold text-violet-700 mb-1 block">From</Label>
                  <Input
                    type="date"
                    value={draft.start_date}
                    onChange={(e) => setDraft({ ...draft, start_date: e.target.value })}
                    className="h-9 text-sm bg-white w-full"
                    data-testid="car-rate-rule-start"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-bold text-violet-700 mb-1 block">To</Label>
                  <Input
                    type="date"
                    value={draft.end_date}
                    onChange={(e) => setDraft({ ...draft, end_date: e.target.value })}
                    className="h-9 text-sm bg-white w-full"
                    data-testid="car-rate-rule-end"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-bold text-violet-700 mb-1 block">Price per day (EUR)</Label>
                <div className="relative max-w-[220px]">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-700 font-bold text-base pointer-events-none">€</span>
                  <Input
                    type="number" step="0.01" min="0"
                    value={draft.daily_price_eur}
                    onChange={(e) => setDraft({ ...draft, daily_price_eur: e.target.value })}
                    placeholder="120.00"
                    className="h-11 text-right font-bold text-base tabular-nums pl-8 pr-12 bg-white border-violet-200 focus-visible:ring-violet-400 focus-visible:ring-2"
                    data-testid="car-rate-rule-price"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-violet-700 text-[11px] font-semibold pointer-events-none">/day</span>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-9 px-3 text-xs"
                  onClick={() => { setAdding(false); setDraft(_newRule()); }}
                  disabled={saving}
                  data-testid="car-rate-rule-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="h-9 px-4 text-xs bg-violet-600 hover:bg-violet-700"
                  onClick={onAdd}
                  disabled={saving}
                  data-testid="car-rate-rule-save"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                  Save rule
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
